import React, { Fragment, useCallback, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import { Checkboxes, Select, TextField } from 'mui-rff';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useSnackbar } from 'notistack';
//
import { createFormFields } from './fields';
import { getInitialForm } from 'components/form/endpoint/utils';
import Loader from 'components/common/Loader';
import ButtonSubmit from 'components/common/ButtonSubmit';
import {
  RadioAdapterAdmFormat,
  RadioAdapterAdType,
  RadioAdapterAdvancedOptions,
  RadioAdapterAllowClickMismatch,
  RadioAdapterAuctionType,
  RadioAdapterCapPubUID,
  RadioAdapterClickDuplicates,
  RadioAdapterClickWithoutImpRule,
  RadioAdapterContentCategoryType,
  RadioAdapterFilterMaskedIP,
  RadioAdapterGateway,
  RadioAdapterHtmlFormat,
  RadioAdapterImpDuplicates,
  RadioAdapterIntegrationType,
  RadioAdapterLookupUserId,
  RadioAdapterMismatchParams,
  RadioAdapterPaymentsWith,
  RadioAdapterProtocol,
  RadioAdapterRtbAdType,
  RadioAdapterRTBVersion,
  RadioAdapterSkipCreativeSize,
  RadioAdapterTrafficType,
  RadioAdapterValidationDefault,
} from 'components/form/endpoint/tabs/common';
import {
  getEndDate,
  getStartDate,
  handleApiError,
  hashids,
  stringToBoolean,
} from 'utils/common';
import {
  composeValidators,
  isInteger,
  isNumber,
  validateDomain,
  maxValue,
  minValue,
  required,
} from 'utils/validate';
import config from '../../../../config';
import EnhancedAutocomplete from 'components/common/EnhancedAutocomplete';
import { UBIDEX_GATEWAY } from '../../../../constants';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useGetWLIDDropdownQuery } from '../../../../store/api/wl.api';
import { useGetPublishersDropdownQuery } from '../../../../store/api/publisher.api';
import {
  useGetEndpointByIdQuery,
  useUpdateEndpointMutation,
} from '../../../../store/api/endpoint.api';

const FlexGrid = styled(Grid)(() => ({
  display: 'flex',
}));
const FlexGridEndItem = styled(FlexGrid)(() => ({
  alignItems: 'flex-end',
}));

const minDiscrepancyValue = minValue(0);
const maxDiscrepancyValue = maxValue(100);

const minPayoutValue = minValue(1);
const maxPayoutValue = maxValue(100);

export default function General({ id, editable }) {
  const isUpdate = useMemo(() => !!id, [id]);
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const { data: endpointDetails, isSuccess: endpointLoaded } =
    useGetEndpointByIdQuery(id, { skip: !isUpdate });
  const generalInfo = useMemo(() => {
    if (!isUpdate) {
      return getInitialForm('oRTB', 'BANNER');
    }
    if (!endpointLoaded) {
      return null;
    }
    return {
      ...endpointDetails,
      gatewayApiUrl: `${config.gatewayApiDomain}/report/gateway?pId=${endpointDetails.id}&key=${endpointDetails.apiKey}&startDate=${getStartDate()}&endDate=${getEndDate()}`,
    };
  }, [endpointLoaded, isUpdate, endpointDetails]);

  const { data: wlids = [], isSuccess: isWlidsLoaded } =
    useGetWLIDDropdownQuery();
  const { data: publishers = [], isSuccess: isPublishersLoaded } =
    useGetPublishersDropdownQuery();

  const [updateEndpoint] = useUpdateEndpointMutation();

  const onChangeLocation = (form) => {
    if (!isUpdate) {
      return;
    }
    const state = form.getState().values;
    let domain = config.usaTraffDomain;
    let route;
    let macros = '';
    const hashIds = hashids.encode([1, state && state.id]);

    if (state && state.location === 'Europe') {
      domain = config.euTraffDomain;
    }

    if (
      state &&
      state.integrationType === 'RTB' &&
      state &&
      state.protocolType === 'oRTB'
    ) {
      switch (state && state.adType) {
        case 'BANNER': {
          route = 'banner';
          break;
        }
        case 'NATIVE': {
          route = 'native';
          break;
        }
        case 'VIDEO': {
          route = 'video';
          break;
        }
        case 'PUSH': {
          route = 'pushrtb';
          break;
        }
        case 'POP': {
          route = 'poprtb';
          break;
        }
        default:
          route = 'banner';
      }
    } else if (
      state &&
      state.integrationType === 'RTB' &&
      state &&
      state.protocolType === 'XML'
    ) {
      switch (state && state.adType) {
        case 'POP': {
          route = 'pop';
          break;
        }
        case 'PUSH': {
          route = 'push';
          break;
        }
        default:
          route = 'pop';
      }
      macros = `&content_type=json&subid={SUB_ID}&query={KEYWORD}&ip={IP}&ua={USER_AGENT}&ref={REFERRER}&lang={BROWSER_LANGUAGE}&sid={SUBSCRIBER_ID}&sdate={SUBSCRIBER_DATE}&limit={LIMIT}&country={COUNTRY}`;
    } else if (state && state.integrationType === 'DIRECT') {
      switch (state && state.adType) {
        case 'POP': {
          route = 'pop';
          break;
        }
        default:
          route = 'pop';
      }
      macros = '&content_type=direct&subid={SUB_ID}&fallback={FALLBACK}';
    }

    form.change(
      'bidderUrl',
      `${domain}/${route}?pid=${state && state.id}&auth=${hashIds}${macros}`,
    );
  };

  const onSubmit = async (values) => {
    let endpointId = id;

    try {
      const result = await updateEndpoint(values).unwrap();
      if (isUpdate) {
        navigate('/endpoints');
      } else {
        endpointId = result.endpoint.id;
        navigate(`/endpoints/${endpointId}/edit`);
      }
      enqueueSnackbar(
        `(${endpointId}) Endpoint ${isUpdate ? 'updated' : 'created'} successfully`,
        { variant: 'success' },
      );
    } catch (e) {
      handleApiError(e);
    }
  };

  const resetForm = (form) => {
    const state = form.getState().values;
    form.initialize(
      getInitialForm(state && state.protocolType, state && state.adType),
    );
  };

  const getWLIDOptionColor = useCallback((option) => {
    switch (option.status) {
      case 'new':
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'removed':
        return 'error';
      default:
        return 'primary';
    }
  }, []);

  if (!generalInfo || !isWlidsLoaded || !isPublishersLoaded) {
    return (
      <CircularProgress
        color='primary'
        size='4rem'
        sx={{ mx: 'auto', my: '2rem', display: 'block' }}
      />
    );
  }
  return (
    <Form
      onSubmit={onSubmit}
      initialValues={generalInfo}
      render={({ handleSubmit, form, submitting, values }) => {
        const state = form.getState().values;
        onChangeLocation(form);

        return (
          <form onSubmit={handleSubmit} noValidate>
            <Typography component='label' variant='h6'>
              Endpoint info
            </Typography>

            <Loader isPending={submitting} />

            <Paper style={{ padding: 16 }}>
              <Grid container spacing={2}>
                <FlexGridEndItem size={{ xs: 6 }}>
                  <Select
                    name='publisherId'
                    label='Publisher *'
                    multiple={false}
                    fieldProps={{ validate: required }}
                    disabled={isUpdate || !editable}
                  >
                    {publishers.map((item, index) => (
                      <MenuItem value={item.value} key={index}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FlexGridEndItem>
                <FlexGridEndItem size={{ xs: 6 }}>
                  <EnhancedAutocomplete
                    name='whitelabels'
                    label='Clients'
                    options={wlids}
                    disabled={!editable}
                    getOptionColor={getWLIDOptionColor}
                    getOptionDisabled={({ integrations, value }) =>
                      !values.whitelabels.includes(value) &&
                      !integrations.includes(UBIDEX_GATEWAY)
                    }
                  />
                </FlexGridEndItem>
              </Grid>
              <br />

              <Grid container alignItems='flex-start' spacing={2}>
                {values &&
                  createFormFields(values, isUpdate, !editable).map(
                    (item, idx) => (
                      <FlexGridEndItem size={{ xs: item.size }} key={idx}>
                        {item.field}
                        {item.isCopy && item.name ? (
                          <CopyToClipboard
                            text={values[item.name]}
                            onCopy={() =>
                              enqueueSnackbar('Copied!', {
                                variant: 'success',
                              })
                            }
                          >
                            <IconButton
                              size='small'
                              color='primary'
                              aria-label='copy'
                            >
                              <FileCopyOutlinedIcon fontSize='small' />
                            </IconButton>
                          </CopyToClipboard>
                        ) : null}
                      </FlexGridEndItem>
                    ),
                  )}
                <FlexGrid size={{ xs: 12 }} alignItems='start'>
                  <FlexGridEndItem size={{ xs: 3 }}>
                    <Field
                      name='isGateway'
                      disabled={!editable}
                      component={RadioAdapterGateway}
                      validate={required}
                    />
                  </FlexGridEndItem>
                </FlexGrid>
              </Grid>
            </Paper>
            <br />

            {state && (
              <Fragment>
                <Typography component='label' variant='h6'>
                  Budget
                </Typography>

                <Paper style={{ padding: 16 }}>
                  <Grid container alignItems='flex-start' spacing={2}>
                    <FlexGridEndItem size={{ xs: 3 }}>
                      <TextField
                        id='dailyBudget'
                        name='dailyBudget'
                        label='Daily budget'
                        fullWidth
                        fieldProps={{
                          parse: (value) => value || null,
                        }}
                        disabled={!editable}
                      />
                    </FlexGridEndItem>
                    <FlexGridEndItem size={{ xs: 3 }}>
                      <TextField
                        id='hourlyBudget'
                        name='hourlyBudget'
                        label='Hourly budget'
                        fullWidth
                        fieldProps={{
                          parse: (value) => value || null,
                        }}
                        disabled={!editable}
                      />
                    </FlexGridEndItem>
                  </Grid>
                  {stringToBoolean(values.isGateway) && (
                    <Grid container alignItems='flex-start' spacing={2}>
                      <FlexGridEndItem size={{ xs: 3 }}>
                        <TextField
                          id='minTopUpAmount'
                          name='minTopUpAmount'
                          label='Min top up amount ($)'
                          fullWidth
                          fieldProps={{
                            validate: isNumber,
                            parse: (value) => value || null,
                          }}
                          disabled={!editable}
                        />
                      </FlexGridEndItem>
                      <FlexGridEndItem size={{ xs: 3 }}>
                        <TextField
                          id='discrepancy'
                          name='discrepancy'
                          label='Discrepancy (%)'
                          fieldProps={{
                            validate: composeValidators(
                              isNumber,
                              minDiscrepancyValue,
                              maxDiscrepancyValue,
                            ),
                            parse: (value) => value || null,
                          }}
                          fullWidth
                          disabled={!editable}
                        />
                      </FlexGridEndItem>
                      <FlexGridEndItem size={{ xs: 3 }}>
                        <TextField
                          id='payout'
                          name='payout'
                          label='Payout (%)'
                          fieldProps={{
                            validate: composeValidators(
                              isInteger,
                              minPayoutValue,
                              maxPayoutValue,
                            ),
                          }}
                          fullWidth
                          disabled={!editable}
                        />
                      </FlexGridEndItem>
                    </Grid>
                  )}
                </Paper>
                <br />
              </Fragment>
            )}

            <Typography component='label' variant='h6'>
              Integration
            </Typography>

            <Paper style={{ padding: 16 }}>
              <Grid container alignItems='flex-start' spacing={2}>
                <FlexGridEndItem size={{ xs: 6 }}>
                  <Field
                    name='integrationType'
                    component={RadioAdapterIntegrationType}
                    validate={required}
                    disabled={!editable}
                  />
                </FlexGridEndItem>
                {state && state.integrationType === 'RTB' ? (
                  <Fragment>
                    <FlexGridEndItem size={{ xs: 6 }}>
                      <Select
                        name='location'
                        label='Data center location'
                        formControlProps={{ margin: 'none' }}
                        disabled={!editable}
                      >
                        <MenuItem value='Usa'>Usa</MenuItem>
                        <MenuItem value='Europe'>Europe</MenuItem>
                      </Select>
                    </FlexGridEndItem>

                    <FlexGridEndItem size={{ xs: 6 }}>
                      <Field
                        name='protocolType'
                        component={RadioAdapterProtocol}
                        validate={required}
                        disabled={!editable}
                      />
                    </FlexGridEndItem>

                    <FlexGridEndItem size={{ xs: 6 }}>
                      <TextField
                        id='bidderUrl'
                        name='bidderUrl'
                        label='Bidder endpoint'
                        fullWidth
                        disabled
                        placeholder='http://endpoints.ubidex.bid'
                      />
                      <CopyToClipboard
                        text={values.bidderUrl}
                        onCopy={() =>
                          enqueueSnackbar('Copied!', { variant: 'success' })
                        }
                      >
                        <IconButton
                          size='small'
                          color='primary'
                          aria-label='copy'
                        >
                          <FileCopyOutlinedIcon fontSize='small' />
                        </IconButton>
                      </CopyToClipboard>
                    </FlexGridEndItem>
                    <FlexGridEndItem size={{ xs: 6 }}>
                      <Field
                        name='adType'
                        component={
                          state?.protocolType === 'oRTB'
                            ? RadioAdapterRtbAdType
                            : RadioAdapterAdType
                        }
                        disabled={!editable}
                        validate={required}
                      />
                    </FlexGridEndItem>

                    {values.isGateway && (
                      <FlexGridEndItem size={{ xs: 6 }}>
                        <TextField
                          name='gatewayApiUrl'
                          label='Statistics url'
                          fullWidth
                          disabled
                          placeholder='http://stats.ugateway.xyz'
                        />
                        <CopyToClipboard
                          text={values.gatewayApiUrl}
                          onCopy={() =>
                            enqueueSnackbar('Copied!', {
                              variant: 'success',
                            })
                          }
                        >
                          <IconButton
                            size='small'
                            color='primary'
                            aria-label='copy'
                          >
                            <FileCopyOutlinedIcon fontSize='small' />
                          </IconButton>
                        </CopyToClipboard>
                      </FlexGridEndItem>
                    )}

                    {!isUpdate && (
                      <FlexGridEndItem size={{ xs: 6 }}>
                        <Button
                          variant='contained'
                          color='primary'
                          size='small'
                          disabled={!editable}
                          onClick={() => resetForm(form)}
                        >
                          Reset
                        </Button>
                      </FlexGridEndItem>
                    )}

                    <FlexGridEndItem size={{ xs: 6 }}>
                      <Select
                        name='deviceType'
                        label='Device Type'
                        formControlProps={{ margin: 'none' }}
                        multiple
                        disabled={!editable}
                      >
                        <MenuItem value='Mobile'>Mobile</MenuItem>
                        <MenuItem value='Tablet'>Tablet</MenuItem>
                        <MenuItem value='Mobile / Tablets'>
                          Mobile / Tablets
                        </MenuItem>
                        <MenuItem value='Desktop'>Desktop</MenuItem>
                        <MenuItem value='Connected TV'>Connected TV</MenuItem>
                      </Select>
                    </FlexGridEndItem>

                    <FlexGridEndItem size={{ xs: 6 }}>
                      <TextField
                        name='proxyDomain'
                        label='Proxy Domain'
                        fullWidth
                        fieldProps={{
                          validate: validateDomain,
                          parse: (value) => value || null,
                        }}
                        disabled={!editable}
                      />
                    </FlexGridEndItem>

                    <FlexGridEndItem size={{ xs: 12 }}>
                      {state && state.protocolType === 'oRTB' ? (
                        <FlexGridEndItem size={{ xs: 3 }}>
                          <Field
                            name='trafficType'
                            component={RadioAdapterTrafficType}
                            disabled={!editable}
                            validate={required}
                          />
                        </FlexGridEndItem>
                      ) : null}
                      <FlexGridEndItem size={{ xs: 4 }}>
                        <Field
                          name='category'
                          component={RadioAdapterContentCategoryType}
                          disabled={!editable}
                          validate={required}
                        />
                      </FlexGridEndItem>
                    </FlexGridEndItem>

                    <FlexGridEndItem size={{ xs: 3 }}>
                      <TextField
                        id='qpsThreshold'
                        name='qpsThreshold'
                        label='Max QPS'
                        fullWidth
                        fieldProps={{
                          parse: (value) => value || null,
                        }}
                        disabled={!editable}
                      />
                    </FlexGridEndItem>

                    <FlexGridEndItem size={{ xs: 12 }}>
                      <Field
                        name='isAdvanced'
                        component={RadioAdapterAdvancedOptions}
                        disabled={!editable}
                        validate={required}
                      />
                    </FlexGridEndItem>
                    {state &&
                    stringToBoolean(String(state && state.isAdvanced)) ? (
                      <Fragment>
                        {state &&
                        state.integrationType === 'RTB' &&
                        state &&
                        state.protocolType === 'oRTB' ? (
                          <Fragment>
                            <FlexGridEndItem size={{ xs: 12 }}>
                              <Field
                                name='auctionType'
                                component={RadioAdapterAuctionType}
                                disabled={!editable}
                                validate={required}
                              />
                            </FlexGridEndItem>
                            <FlexGridEndItem size={{ xs: 3 }}>
                              <TextField
                                id='tmax'
                                name='tmax'
                                label='Min timeout (tmax), ms'
                                fullWidth
                                fieldProps={{
                                  parse: (value) => value || null,
                                }}
                                disabled={!editable}
                              />
                            </FlexGridEndItem>
                            <FlexGridEndItem size={{ xs: 9 }} />
                            <FlexGridEndItem size={{ xs: 3 }}>
                              <TextField
                                id='defaultBidfloor'
                                name='defaultBidfloor'
                                label='Default bidfloor'
                                fullWidth
                                fieldProps={{
                                  parse: (value) => value || null,
                                }}
                                disabled={!editable}
                              />
                            </FlexGridEndItem>
                            <FlexGridEndItem size={{ xs: 9 }} />
                            <FlexGridEndItem size={{ xs: 12 }}>
                              <Field
                                name='paymentsWith'
                                component={RadioAdapterPaymentsWith}
                                disabled={!editable}
                                validate={required}
                              />
                            </FlexGridEndItem>
                            <FlexGridEndItem size={{ xs: 12 }}>
                              {state.protocolType === 'oRTB' &&
                              ['POP', 'PUSH', 'NATIVE'].includes(
                                state.adType,
                              ) ? null : state.adType === 'VIDEO' ? (
                                <Field
                                  name='admFormat'
                                  component={RadioAdapterAdmFormat}
                                  disabled={!editable}
                                  validate={required}
                                />
                              ) : (
                                <Field
                                  name='isHtmlFormat'
                                  component={RadioAdapterHtmlFormat}
                                  disabled={!editable}
                                  validate={required}
                                />
                              )}
                            </FlexGridEndItem>
                            <FlexGridEndItem size={{ xs: 12 }}>
                              {state.protocolType === 'oRTB' &&
                              state.adType === 'NATIVE' ? (
                                <Field
                                  name='responseFormat'
                                  component={RadioAdapterRTBVersion}
                                  disabled={!editable}
                                  validate={required}
                                />
                              ) : null}
                            </FlexGridEndItem>
                          </Fragment>
                        ) : (
                          <FlexGridEndItem size={{ xs: 3 }}>
                            <TextField
                              id='tmax'
                              name='tmax'
                              label='Min timeout (tmax), ms'
                              fullWidth
                              fieldProps={{
                                parse: (value) => value || null,
                              }}
                              disabled={!editable}
                            />
                          </FlexGridEndItem>
                        )}
                      </Fragment>
                    ) : null}
                    <FlexGridEndItem size={{ xs: 6 }} />
                    <FlexGridEndItem size={{ xs: 6 }} />
                  </Fragment>
                ) : null}
              </Grid>
            </Paper>
            <br />

            {state ? (
              <Fragment>
                <Typography component='label' variant='h6'>
                  Validation
                </Typography>
                <Paper style={{ padding: 16 }}>
                  <Grid container alignItems='flex-start' spacing={2}>
                    <FlexGridEndItem size={{ xs: 12 }}>
                      <Field
                        name='isValidationDefault'
                        component={RadioAdapterValidationDefault}
                        disabled={!editable}
                        validate={required}
                      />
                    </FlexGridEndItem>

                    {state &&
                    !stringToBoolean(state && state.isValidationDefault) ? (
                      <Fragment>
                        <FlexGridEndItem size={{ xs: 6 }}>
                          <TextField
                            id='impressionTtl'
                            name='impressionTtl'
                            label='Impression TTL (minutes)'
                            fullWidth
                            fieldProps={{
                              parse: (value) => value || null,
                            }}
                            disabled={!editable}
                          />
                        </FlexGridEndItem>
                        <FlexGridEndItem size={{ xs: 6 }}>
                          <TextField
                            id='maxErrorRate'
                            name='maxErrorRate'
                            label='Max Error Rate'
                            fullWidth
                            fieldProps={{
                              parse: (value) => value || null,
                            }}
                            disabled={!editable}
                          />
                        </FlexGridEndItem>
                        <FlexGridEndItem size={{ xs: 6 }}>
                          <TextField
                            id='maxNoMatchRate'
                            name='maxNoMatchRate'
                            label='Max no match rate'
                            placeholder='(0-100%)'
                            fullWidth
                            fieldProps={{
                              parse: (value) => value || null,
                            }}
                            disabled={!editable}
                          />
                        </FlexGridEndItem>
                        <FlexGridEndItem size={{ xs: 6 }}>
                          {state && state.adType !== 'POP' ? (
                            <TextField
                              id='clickTtl'
                              name='clickTtl'
                              label='Click TTL (hours)'
                              fullWidth
                              fieldProps={{
                                parse: (value) => value || null,
                              }}
                              disabled={!editable}
                            />
                          ) : null}
                        </FlexGridEndItem>
                        <FlexGridEndItem size={{ xs: 6 }}>
                          <Field
                            name='isAllowImpDuplicates'
                            component={RadioAdapterImpDuplicates}
                            disabled={!editable}
                            validate={required}
                          />
                        </FlexGridEndItem>
                        <FlexGridEndItem size={{ xs: 6 }}>
                          {state && state.adType !== 'POP' ? (
                            <Field
                              name='isAllowClickDuplicates'
                              component={RadioAdapterClickDuplicates}
                              disabled={!editable}
                              validate={required}
                            />
                          ) : null}
                        </FlexGridEndItem>
                        <FlexGridEndItem size={{ xs: 6 }}>
                          <Field
                            name='isFilterMaskedIP'
                            component={RadioAdapterFilterMaskedIP}
                            disabled={!editable}
                            validate={required}
                          />
                        </FlexGridEndItem>
                        <FlexGridEndItem size={{ xs: 6 }}>
                          {state && state.adType !== 'POP' ? (
                            <Field
                              name='isClickWithoutImpRule2'
                              component={RadioAdapterClickWithoutImpRule}
                              disabled={!editable}
                              validate={required}
                            />
                          ) : null}
                        </FlexGridEndItem>
                        <FlexGridEndItem size={{ xs: 6 }}>
                          <Field
                            name='isLookupUserId'
                            component={RadioAdapterLookupUserId}
                            disabled={!editable}
                            validate={required}
                          />
                        </FlexGridEndItem>
                        <FlexGridEndItem size={{ xs: 6 }}>
                          <Field
                            name='isSkipCreativeSize'
                            component={RadioAdapterSkipCreativeSize}
                            disabled={!editable}
                            validate={required}
                          />
                        </FlexGridEndItem>
                        <FlexGridEndItem size={{ xs: 6 }}>
                          <Field
                            name='isCapPubUID'
                            component={RadioAdapterCapPubUID}
                            disabled={!editable}
                            validate={required}
                          />
                        </FlexGridEndItem>
                        <FlexGridEndItem size={{ xs: 6 }} />
                        <FlexGridEndItem size={{ xs: 6 }}>
                          {state && state.protocolType === 'oRTB' ? (
                            <Checkboxes
                              label='Required bid requests parameters'
                              name='bidParams'
                              disabled={!editable}
                              formControlProps={{ disabled: !editable }}
                              data={[
                                { label: 'SubID', value: 'isSubID' },
                                { label: 'TagID', value: 'isTagID' },
                              ]}
                            />
                          ) : (
                            <Checkboxes
                              label='Required bid requests parameters'
                              name='bidParams'
                              disabled={!editable}
                              formControlProps={{ disabled: !editable }}
                              data={[
                                { label: 'SubID', value: 'isSubID' },
                                {
                                  label: 'SubscriberID (sid)',
                                  value: 'isSubscriberID',
                                },
                                {
                                  label: 'SubscriberDate (sdate)',
                                  value: 'isSubscriberDate',
                                },
                              ]}
                            />
                          )}
                        </FlexGridEndItem>

                        <FlexGridEndItem size={{ xs: 6 }} />
                        <FlexGridEndItem size={{ xs: 6 }}>
                          <Field
                            name='isAllowMismatch'
                            component={RadioAdapterMismatchParams}
                            disabled={!editable}
                            validate={required}
                          />
                        </FlexGridEndItem>
                        {state && state.adType !== 'POP' && (
                          <FlexGridEndItem size={{ xs: 6 }}>
                            <Field
                              name='isAllowClickMismatch'
                              component={RadioAdapterAllowClickMismatch}
                              disabled={!editable}
                              validate={required}
                            />
                          </FlexGridEndItem>
                        )}
                      </Fragment>
                    ) : null}
                  </Grid>
                </Paper>
              </Fragment>
            ) : null}

            <Grid container alignItems='flex-start' spacing={2}>
              <Grid style={{ marginTop: 16 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  <ButtonSubmit
                    name='save'
                    disabled={!editable}
                    onSubmit={handleSubmit}
                    isPending={submitting}
                    type='submit'
                  />
                </Box>
              </Grid>
            </Grid>
          </form>
        );
      }}
    />
  );
}
